import MinusCircleOutlined from '@ant-design/icons/MinusCircleOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import { Page, Text, View } from '@react-pdf/renderer'
import { Button, DatePicker, Form, Input, message, Space } from 'antd'
import dayjs from 'dayjs'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { InspectionTeamMemberData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'pages/reporting/shared/pdfGeneration/utils/formatters'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useMemo } from 'react'

const requiredInspectionTeamFields = ['inspectionTeamMembers']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(
    () => formData.inspectionTeamMemberData,
    [formData.inspectionTeamMemberData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page size='A4' style={PDFStyles.page} id='inspection-team' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'inspection-team', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Inspection Team Members' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.inspectionTeamMembers &&
          data.inspectionTeamMembers.length > 0 ? (
            <>
              {/* Table Header */}
              <View
                style={{
                  flexDirection: 'row',
                  backgroundColor: '#E5E7EB',
                  borderBottom: '1 solid #9CA3AF',
                  padding: 8,
                }}>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '30%' }}>
                  Full Name
                </Text>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '25%' }}>
                  Applicable Certification
                </Text>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '25%' }}>
                  Certification ID
                </Text>
                <Text style={{ fontSize: 10, fontWeight: 700, width: '20%' }}>
                  Expiration Date
                </Text>
              </View>

              {/* Table Rows */}
              {data.inspectionTeamMembers.map((member, index) => (
                <View
                  key={index}
                  style={{
                    flexDirection: 'row',
                    borderBottom: '1 solid #E5E7EB',
                    padding: 8,
                    backgroundColor: index % 2 === 0 ? 'white' : '#F9FAFB',
                  }}>
                  <Text style={{ fontSize: 9, width: '30%' }}>
                    {member.name}
                  </Text>
                  <Text style={{ fontSize: 9, width: '25%' }}>
                    {member.certification.name}
                  </Text>
                  <Text style={{ fontSize: 9, width: '25%' }}>
                    {member.certification.id}
                  </Text>
                  <Text style={{ fontSize: 9, width: '20%' }}>
                    {member.certification.expirationDate
                      ? formatDate(dayjs(member.certification.expirationDate))
                      : 'N/A'}
                  </Text>
                </View>
              ))}
            </>
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<InspectionTeamMemberData>()
  const { formData, handleInspectionTeamFinish } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: InspectionTeamMemberData) => {
      try {
        // Convert DatePicker moments to ISO strings
        const members =
          values.inspectionTeamMembers?.map((member) => ({
            ...member,
            certification: {
              ...member.certification,
              expirationDate: member.certification.expirationDate
                ? dayjs(member.certification.expirationDate).toISOString()
                : '',
            },
          })) || []

        await handleInspectionTeamFinish(members)
        message.success('Inspection team saved successfully!')
      } catch {
        message.error('Failed to save inspection team')
      }
    },
    [handleInspectionTeamFinish]
  )

  const initialValues = React.useMemo(
    () => ({
      inspectionTeamMembers:
        formData.inspectionTeamMemberData.inspectionTeamMembers?.map(
          (member) => ({
            ...member,
            certification: {
              ...member.certification,
              expirationDate: member.certification.expirationDate
                ? dayjs(member.certification.expirationDate)
                : undefined,
            },
          })
        ) || [],
    }),
    [formData.inspectionTeamMemberData]
  )

  return (
    <StyledForm<InspectionTeamMemberData>
      form={form}
      name='inspectionTeamForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.List name='inspectionTeamMembers'>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Space
                key={key}
                style={{
                  display: 'flex',
                  marginBottom: 8,
                  border: '1px solid #d9d9d9',
                  padding: 16,
                  borderRadius: 8,
                }}
                align='baseline'>
                <Form.Item
                  {...restField}
                  name={[name, 'name']}
                  label='Full Name'
                  rules={[{ required: true, message: 'Missing name' }]}>
                  <Input
                    placeholder='John Doe ~ john.doe@company.com'
                    style={{ width: 250 }}
                  />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'certification', 'name']}
                  label='Certification'
                  rules={[
                    { required: true, message: 'Missing certification' },
                  ]}>
                  <Input placeholder='API 570' />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'certification', 'id']}
                  label='Certification ID'
                  rules={[
                    { required: true, message: 'Missing certification ID' },
                  ]}>
                  <Input placeholder='88944' />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'certification', 'expirationDate']}
                  label='Expiration Date'
                  rules={[
                    { required: true, message: 'Missing expiration date' },
                  ]}>
                  <DatePicker />
                </Form.Item>

                <MinusCircleOutlined onClick={() => remove(name)} />
              </Space>
            ))}

            <Form.Item>
              <Button
                type='dashed'
                onClick={() => add()}
                block
                icon={<PlusOutlined />}>
                Add Team Member
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Inspection Team
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const InspectionTeamSection: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: 'inspectionTeam',
  description: 'Team members and certifications',
  title: 'Inspection Team',
  requiredFields: requiredInspectionTeamFields,
}

export const InspectionTeamSectionPDF = RenderPDF
